// eslint-disable-next-line no-shadow
var GLOBAL_EVENTS;
(function (GLOBAL_EVENTS) {
  GLOBAL_EVENTS["DOCUMENT_CHANGED"] = "documentChanged";
  GLOBAL_EVENTS["DOCUMENT_RESIZED"] = "documentResized";
  GLOBAL_EVENTS["THEME_CHANGED"] = "themeChanged";
})(GLOBAL_EVENTS || (GLOBAL_EVENTS = {}));
const OWC_GLOBAL_KEY = 'owc';
const RUNTIME_FWK_GLOBAL_KEY = 'fwk';
const RUNTIME_FWK_VERSION_GLOBAL_KEY = 'fwk-version';
const RUNTIME_OWC_VERSION_GLOBAL_KEY = 'owc-version';
const DOC_MUTATION_OBSERVER_GLOBAL_KEY = 'documentMutationObserver';
const DOC_RESIZE_OBSERVER_GLOBAL_KEY = 'documentResizeObserver';
const SCREEN_GLOBAL_KEY = 'screen';
const SCREEN_WIDTH_GLOBAL_KEY = 'width';
const SCREEN_SIZE_GLOBAL_KEY = 'size';
const PERFORMANCE_API_GLOBAL_KEY = 'performance';
const PERFORMANCE_API_STORAGE_KEY = 'owc-performance';
const SUBSCRIPTIONS_KEY = 's-subscriptions';
const CTX_KEY = 's-ctx';
const deepMerge = (...sources) => {
  let result = {};
  for (const source of sources) {
    if (typeof source !== 'object') {
      continue;
    }
    if (Array.isArray(source)) {
      const copiedSource = source.map(item => typeof item === 'object' ? deepMerge(item) : item);
      if (Array.isArray(result)) {
        result.push(...copiedSource);
      } else {
        result = copiedSource;
      }
    } else {
      for (const key in source) {
        result[key] = typeof source[key] === 'object' ? deepMerge(result[key], source[key]) : source[key];
      }
    }
  }
  return result;
};
const isDefined = v => v !== undefined && v !== null;
const isNumberAndDefined = (v, ownCondition = true) => isDefined(v) && typeof v === 'number' && !isNaN(v) && ownCondition;
const isStringAndDefined = v => isDefined(v) && typeof v === 'string';
const isParsableFloat = v => !isNaN(parseFloat(v)) && parseFloat(v) >= 0 && /[0-9]*\.?[0-9]+/.test(v);
const isParsableNumber = v => !isNaN(parseInt(v, 10)) && parseInt(v, 10) >= 0 && /[0-9]+$/.test(v);
const isString = v => isDefined(v) && typeof v === 'string' && v.trim().length !== 0;
const isObject = v => isDefined(v) && typeof v === 'object';
const isEmptyObject = v => isObject(v) && Object.keys(v).length === 0;
const hasValue = v => {
  if (!isDefined(v)) {
    return false;
  }
  if (Array.isArray(v)) {
    return v.length !== 0;
  }
  if (typeof v === 'object') {
    return Object.keys(v).length > 0;
  }
  if (typeof v === 'string') {
    return !!v.trim();
  }
  if (typeof v === 'number') {
    return !Number.isNaN(v);
  }
  return true;
};
const isPositiveFloat = v => isNumberAndDefined(v, +v >= 0) || isStringAndDefined(v) && isParsableFloat(v);
const isFloat = v => isNumberAndDefined(v) || isStringAndDefined(v) && !isNaN(parseFloat(v)) && /[0-9]*\.?[0-9]+/.test(v);
const isPositiveNumber = v => isNumberAndDefined(v, +v >= 0) || isStringAndDefined(v) && isParsableNumber(v);
const isNumber = v => isNumberAndDefined(v) || isStringAndDefined(v) && !isNaN(parseInt(v, 10)) && /[0-9]+$/.test(v);
const DEFAULT_LENGTH = 16;
const randomString = (length = DEFAULT_LENGTH) => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from(new Array(length)).map(() => characters.charAt(Math.floor(Math.random() * characters.length))).join('');
};
const stringToNumberArray = (arg, separator = ',') => {
  const opts = typeof arg === 'string' ? arg.split(separator).map(p => p.trim()).filter(hasValue) : arg;
  return opts && opts.length ? opts.filter(isNumber).map(opt => +opt) : []; // prevent empty string error
};
const stringToObject = arg => isString(arg) ? JSON.parse(arg) : arg;
const isAlphaNumeric = arg => !!arg.match(/^[0-9A-Za-z]+$/);
const isSSR = () => typeof window === 'undefined';
class OwcPerformanceApi {
  constructor() {
    this._isStatusLogged = false;
  }
  logStatus() {
    if (!this._isStatusLogged) {
      if (this.isEnabled()) {
        console.log('OWC PERFORMANCE API is enabled!!!!');
      }
      this._isStatusLogged = true;
    }
    return this;
  }
  enable() {
    if (!isSSR()) {
      window.sessionStorage.setItem(PERFORMANCE_API_STORAGE_KEY, JSON.stringify(true));
      window.location.reload();
    }
    return this;
  }
  disable() {
    if (!isSSR()) {
      window.sessionStorage.removeItem(PERFORMANCE_API_STORAGE_KEY);
      window.location.reload();
    }
    return this;
  }
  isEnabled() {
    if (!isSSR()) {
      const rawValue = window.sessionStorage.getItem(PERFORMANCE_API_STORAGE_KEY);
      try {
        const typedValue = JSON.parse(rawValue);
        return typedValue === true;
      } catch (_a) {
        // nothing to do
      }
    }
    return false;
  }
}
if (!isSSR()) {
  !(OWC_GLOBAL_KEY in window) && (window[OWC_GLOBAL_KEY] = {});
  if (!(PERFORMANCE_API_GLOBAL_KEY in window[OWC_GLOBAL_KEY])) {
    window[OWC_GLOBAL_KEY][PERFORMANCE_API_GLOBAL_KEY] = new OwcPerformanceApi();
  }
  window[OWC_GLOBAL_KEY][PERFORMANCE_API_GLOBAL_KEY].logStatus();
}
const winObjSafe = !isSSR() ? window : {};
/* eslint-disable dot-notation */
const mozRaf = !isSSR() && winObjSafe['mozRequestAnimationFrame'] && winObjSafe['mozCancelRequestAnimationFrame']; // Firefox 5 ships without cancel support
const browserSupportsRaf = !isSSR() && !!(winObjSafe['requestAnimationFrame'] || winObjSafe['webkitRequestAnimationFrame'] || mozRaf || winObjSafe['oRequestAnimationFrame'] || winObjSafe['msRequestAnimationFrame']);
const requestTimeoutNoRaf = (fn, delay) => {
  const timeoutId = setTimeout(fn, delay);
  return () => clearTimeout(timeoutId);
};
const requestTimeoutRaf = (fn, delay) => {
  const start = new Date().getTime();
  let rafId;
  const loop = () => {
    const delta = new Date().getTime() - start;
    if (delta >= (delay || 0)) {
      fn();
      rafId = undefined;
      return;
    }
    rafId = requestAnimationFrame(loop);
  };
  rafId = requestAnimationFrame(loop);
  return () => {
    rafId && cancelAnimationFrame(rafId);
  };
};
const requestTimeout = browserSupportsRaf ? requestTimeoutRaf : requestTimeoutNoRaf;

/* eslint-disable no-shadow */
var NodeType;
(function (NodeType) {
  NodeType[NodeType["ELEMENT"] = 1] = "ELEMENT";
  NodeType[NodeType["ATTRIBUTE"] = 2] = "ATTRIBUTE";
  NodeType[NodeType["TEXT"] = 3] = "TEXT";
  NodeType[NodeType["CDATA_SECTION"] = 4] = "CDATA_SECTION";
  NodeType[NodeType["ENTITY_REFERENCE"] = 5] = "ENTITY_REFERENCE";
  NodeType[NodeType["ENTITY"] = 6] = "ENTITY";
  NodeType[NodeType["PROCESSING_INSTRUCTION"] = 7] = "PROCESSING_INSTRUCTION";
  NodeType[NodeType["COMMENT"] = 8] = "COMMENT";
  NodeType[NodeType["DOCUMENT"] = 9] = "DOCUMENT";
  NodeType[NodeType["DOCUMENT_TYPE"] = 10] = "DOCUMENT_TYPE";
  NodeType[NodeType["DOCUMENT_FRAGMENT"] = 11] = "DOCUMENT_FRAGMENT";
  NodeType[NodeType["NOTATION"] = 12] = "NOTATION";
})(NodeType || (NodeType = {}));
const DURATION_DEFAULT = 20;
const COUNTER_DEFAULT = 15;
const getDocument = () => typeof document !== 'undefined' ? document : undefined;
const getBody = () => {
  var _a;
  return (_a = getDocument()) === null || _a === void 0 ? void 0 : _a.body;
};
const getComputedStyleSafe = (elt, pseudoElt) => !isSSR() && typeof getComputedStyle === 'function' ? getComputedStyle(elt, pseudoElt) : undefined;
const hasEllipsis = element => element && (element === null || element === void 0 ? void 0 : element.offsetWidth) < (element === null || element === void 0 ? void 0 : element.scrollWidth);
const generateElementId = el => {
  var _a;
  return [(_a = typeof el === 'string' ? el : el === null || el === void 0 ? void 0 : el.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase(), randomString()].filter(Boolean).join('-');
};
const isElementReady = async (cb, counter = COUNTER_DEFAULT, duration = DURATION_DEFAULT) => {
  let i = 0;
  while (!cb() && i < counter) {
    await new Promise(resolve => setTimeout(resolve, duration));
    i++;
  }
  return cb();
};
const isElementVisible = element => {
  const style = getComputedStyleSafe(element);
  if (style) {
    if (style.display === 'none') {
      return false;
    }
    if (style.visibility !== 'visible') {
      return false;
    }
    if (element.offsetWidth + element.offsetHeight + element.getBoundingClientRect().height + element.getBoundingClientRect().width === 0) {
      return false;
    }
  }
  return true;
};
const isElementInDocument = (doc, element) => {
  let currentElement = element;
  while (currentElement && currentElement.parentNode && isElementVisible(element)) {
    if (currentElement.parentNode === doc) {
      return true;
    } else if (currentElement.parentNode instanceof ShadowRoot) {
      currentElement = currentElement.parentNode.host;
    } else {
      currentElement = currentElement.parentNode;
    }
  }
  return false;
};
const setElementAttributes = (element, attributes) => {
  if (!element) {
    return;
  }
  const keys = Object.keys(attributes || {});
  for (const key of keys) {
    if (key.startsWith('on')) {
      const eventName = key.replace('on', '');
      element.addEventListener(`${eventName[0].toLowerCase()}${eventName.slice(1)}`, attributes[key]);
    } else if (key.startsWith('style')) {
      for (const [variable, value] of Object.entries(attributes[key])) {
        element.style.setProperty(variable, value);
      }
    } else {
      if (key.startsWith('class')) {
        element.classList.add(attributes[key]);
      } else {
        element[key] = attributes[key];
      }
    }
  }
};
const getSiblings = (element, filter = () => true) => {
  var _a;
  let iterator = (_a = element === null || element === void 0 ? void 0 : element.parentElement) === null || _a === void 0 ? void 0 : _a.firstElementChild;
  const siblings = [];
  for (; iterator; iterator = iterator.nextElementSibling) {
    if (iterator.nodeType === NodeType.ELEMENT && (filter === null || filter === void 0 ? void 0 : filter(iterator))) {
      siblings.push(iterator);
    }
  }
  return siblings;
};
const getRelativePosition = (mousePos, parent) => {
  const bounds = parent === null || parent === void 0 ? void 0 : parent.getBoundingClientRect();
  return {
    x: ((mousePos === null || mousePos === void 0 ? void 0 : mousePos.x) || 0) - ((bounds === null || bounds === void 0 ? void 0 : bounds.left) || 0),
    y: ((mousePos === null || mousePos === void 0 ? void 0 : mousePos.y) || 0) - ((bounds === null || bounds === void 0 ? void 0 : bounds.top) || 0)
  };
};

/* eslint-disable no-shadow */
var DropzoneBehavior;
(function (DropzoneBehavior) {
  DropzoneBehavior["APPEND"] = "append";
  DropzoneBehavior["PREPEND"] = "prepend";
})(DropzoneBehavior || (DropzoneBehavior = {}));
var DropBehavior;
(function (DropBehavior) {
  DropBehavior["FLOATING"] = "floating";
  DropBehavior["MOVE"] = "move";
  DropBehavior["COPY"] = "copy";
})(DropBehavior || (DropBehavior = {}));
const UNIQUE_KEY_LEN = 16;
const DRAGGING_CLASS = 'dragging';
const DROPZONE_CLASS = 'dropzone-element';
const DROPZONE_ACTIVE_CLASS = 'dropzone-element-active';
const DRAGGABLE_KEY = 'draggable';
const DRAGGABLE_UNIQUE_DATA_KEY = 'uniqueData';
const DROP_BEHAVIOR_KEY = 'dropBehavior';
const DROPZONE_BEHAVIOR_KEY = 'dropzoneBehavior';
const DEFAULT_DRAGGABLE_OPTIONS = {
  dropBehavior: DropBehavior.FLOATING
};
const doIf = (condition, fn) => condition && fn();
const getMin = (i1, i2) => typeof i2 === 'number' ? Math.min(i1, i2) : i1;
const getMax = (i1, i2) => typeof i2 === 'number' ? Math.max(i1, i2) : i1;
class DraggableElement {
  constructor(el, opts = DEFAULT_DRAGGABLE_OPTIONS) {
    this.el = el;
    this.options = DEFAULT_DRAGGABLE_OPTIONS;
    this.moving = false;
    this.firstMoveoccurred = false;
    this.currentDroppable = null;
    this.initialStyles = {};
    this.options = Object.assign(Object.assign({}, DEFAULT_DRAGGABLE_OPTIONS), opts);
    this.onMove = this.onMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onMouseDown = this.onMouseDown.bind(this);
  }
  static from(el, opts) {
    return new DraggableElement(el, opts);
  }
  makeDraggable() {
    doIf(this.el, () => {
      this.el.dataset[DROP_BEHAVIOR_KEY] = this.options.dropBehavior;
      this.el.dataset[DRAGGABLE_KEY] = 'true';
      this.initialStyles = {
        position: this.el.style.position,
        top: this.el.style.top,
        left: this.el.style.left,
        zIndex: this.el.style.zIndex
      };
      this.el.ondragstart = () => false;
      this.el.onmousedown = this.onMouseDown;
    });
  }
  onMouseDown() {
    var _a, _b;
    doIf((_b = (_a = this.el) === null || _a === void 0 ? void 0 : _a.dataset) === null || _b === void 0 ? void 0 : _b[DRAGGABLE_KEY], () => {
      var _a, _b, _c;
      doIf(!((_b = (_a = this.el) === null || _a === void 0 ? void 0 : _a.dataset) === null || _b === void 0 ? void 0 : _b[DRAGGABLE_UNIQUE_DATA_KEY]), () => this.setUniqueData(this.el));
      this.moving = true;
      this.firstMoveoccurred = false;
      (_c = getDocument()) === null || _c === void 0 ? void 0 : _c.addEventListener('mousemove', this.onMove);
      this.el.onmouseup = this.onMouseUp;
    });
  }
  onMove(ev) {
    doIf(this.moving, () => {
      var _a;
      doIf(!this.el.classList.contains(DRAGGING_CLASS), () => this.el.classList.add(DRAGGING_CLASS));
      doIf(!this.firstMoveoccurred, () => {
        this.firstMoveoccurred = true;
        this.emitDragEvent('dragstart');
      });
      const pos = getRelativePosition({
        x: ev.clientX,
        y: ev.clientY
      }, getBody());
      this.setPosition(pos);
      this.emitDragEvent();
      const dropBehavior = (_a = this.el.dataset) === null || _a === void 0 ? void 0 : _a[DROP_BEHAVIOR_KEY];
      doIf(dropBehavior && dropBehavior !== DropBehavior.FLOATING, () => {
        var _a, _b, _c;
        const droppableElement = (_a = getDocument()) === null || _a === void 0 ? void 0 : _a.elementsFromPoint(pos.x, pos.y).find(elem => {
          if (elem.classList.contains(DROPZONE_CLASS)) {
            return typeof this.options.isValidDropzone === 'function' ? this.options.isValidDropzone(elem) : true;
          } else {
            return false;
          }
        });
        if (!droppableElement) {
          (_b = this.currentDroppable) === null || _b === void 0 ? void 0 : _b.classList.remove(DROPZONE_ACTIVE_CLASS);
          this.currentDroppable = null;
        } else {
          doIf(!((_c = this.currentDroppable) === null || _c === void 0 ? void 0 : _c.isSameNode(droppableElement)), () => {
            var _a;
            (_a = this.currentDroppable) === null || _a === void 0 ? void 0 : _a.classList.remove(DROPZONE_ACTIVE_CLASS);
            this.currentDroppable = droppableElement;
          });
          !this.currentDroppable.classList.contains(DROPZONE_ACTIVE_CLASS) && this.currentDroppable.classList.add(DROPZONE_ACTIVE_CLASS);
        }
      });
    });
  }
  onMouseUp() {
    var _a;
    (_a = getDocument()) === null || _a === void 0 ? void 0 : _a.removeEventListener('mousemove', this.onMove);
    doIf(this.moving && this.el, () => {
      var _a;
      this.el.classList.remove(DRAGGING_CLASS);
      this.emitDragEvent('dragend');
      const dropBehavior = (_a = this.el.dataset) === null || _a === void 0 ? void 0 : _a[DROP_BEHAVIOR_KEY];
      doIf(dropBehavior && dropBehavior !== DropBehavior.FLOATING, () => {
        this.resetDefaultStyles();
        doIf(this.currentDroppable, () => {
          var _a, _b, _c;
          (_a = this.currentDroppable.classList) === null || _a === void 0 ? void 0 : _a.remove(DROPZONE_ACTIVE_CLASS);
          const dropzoneBehavior = (_c = (_b = this.currentDroppable) === null || _b === void 0 ? void 0 : _b.dataset) === null || _c === void 0 ? void 0 : _c[DROPZONE_BEHAVIOR_KEY];
          if (dropzoneBehavior === DropzoneBehavior.APPEND) {
            this.appendToDropzone(dropBehavior);
          }
          if (dropzoneBehavior === DropzoneBehavior.PREPEND) {
            this.prependToDropzone(dropBehavior);
          }
        });
      });
    });
    this.moving = false;
    this.firstMoveoccurred = false;
    this.el.onmouseup = null;
    this.currentDroppable = null;
  }
  appendToDropzone(dropBehavior) {
    doIf(this.el, () => {
      const clonedElement = this.el.cloneNode(true);
      doIf(dropBehavior === DropBehavior.MOVE, () => {
        this.currentDroppable.append(clonedElement);
        this.el.remove();
      });
      doIf(dropBehavior === DropBehavior.COPY, () => {
        this.setUniqueData(clonedElement);
        this.currentDroppable.append(clonedElement);
      });
    });
  }
  prependToDropzone(dropBehavior) {
    doIf(this.el, () => {
      const clonedElement = this.el.cloneNode(true);
      doIf(dropBehavior === DropBehavior.MOVE, () => {
        this.currentDroppable.prepend(clonedElement);
        this.el.remove();
      });
      doIf(dropBehavior === DropBehavior.COPY, () => {
        this.setUniqueData(clonedElement);
        this.currentDroppable.prepend(clonedElement);
      });
    });
  }
  setPosition(pos) {
    doIf(this.moving && this.el, () => {
      var _a, _b, _c, _d, _e, _f, _g, _h;
      const rect = this.el.getBoundingClientRect();
      const multiplier = 0.5;
      const left = getMin(getMax(pos.x - rect.width * multiplier, (_b = (_a = this.options.boundries) === null || _a === void 0 ? void 0 : _a.min) === null || _b === void 0 ? void 0 : _b.x), (_d = (_c = this.options.boundries) === null || _c === void 0 ? void 0 : _c.max) === null || _d === void 0 ? void 0 : _d.x);
      const top = getMin(getMax(pos.y - rect.height * multiplier, (_f = (_e = this.options.boundries) === null || _e === void 0 ? void 0 : _e.min) === null || _f === void 0 ? void 0 : _f.y), (_h = (_g = this.options.boundries) === null || _g === void 0 ? void 0 : _g.max) === null || _h === void 0 ? void 0 : _h.y);
      this.el.style.position = 'absolute';
      this.el.style.left = `${left}px`;
      this.el.style.top = `${top}px`;
      this.el.style.zIndex = `9999999999999999`;
    });
  }
  setUniqueData(element) {
    doIf(element, () => {
      element.dataset[DRAGGABLE_UNIQUE_DATA_KEY] = `${element.tagName.toLowerCase()}-${randomString(UNIQUE_KEY_LEN)}`;
    });
  }
  resetDefaultStyles() {
    doIf(this.el, () => Object.keys(this.initialStyles).forEach(key => this.el.style.setProperty(key, this.initialStyles[key])));
  }
  emitDragEvent(type = 'drag', bubbles = false, cancelable = true) {
    const e = new Event(type, {
      bubbles,
      cancelable
    });
    this.el.dispatchEvent(e);
  }
}
/**
 * Makes element draggable.
 *
 * @param el target element to be made draggable
 * @param opts additional options for drag and drop behavior
 *
 * @example
 * // element can be placed anywhere in the page
 * makeDraggable(document.createElement('div'), { dropBehavior: DropBehavior.FLOATING })
 *
 * // element will be moved to the dropzone
 * makeDraggable(document.createElement('div'), { dropBehavior: DropBehavior.MOVE })
 *
 * // element will be copied to the dropzone
 * makeDraggable(document.createElement('div'), { dropBehavior: DropBehavior.COPY })
 *
 * // element cannot be dragged the position where less then 30px in x-axis and 40px in y-axes
 * makeDraggable(document.createElement('div'), { boundries: { min: { x: 30, y: 40 } } })
 *
 * // element cannot be dragged the position where more then 80px in x-axis and 90px in y-axes
 * makeDraggable(document.createElement('div'), { boundries: { max: { x: 80, y: 90 } } })
 *
 * // element cannot be dropped the dropzone which has no id
 * makeDraggable(document.createElement('div'), {
 *   dropBehavior: DropBehavior.MOVE,
 *   isValidDropzone: zone => !!zone.id
 * })
 */
const makeDraggable = (el, opts) => {
  DraggableElement.from(el, opts).makeDraggable();
};
/**
 * Makes element droppable.
 *
 * @param el target element to be made draggable
 * @param behavior defines the element adding behavior of the dropzone
 *
 * @example
 * // adds dragged element into the dropzone by appending it
 * makeDropzone(document.createElement('div'), DropzoneBehavior.APPEND)
 *
 * // adds dragged element into the dropzone by prepending it
 * makeDropzone(document.createElement('div'), DropzoneBehavior.PREPEND)
 */
const makeDropzone = (el, behavior = DropzoneBehavior.APPEND) => {
  el.classList.add(DROPZONE_CLASS);
  el.dataset[DROPZONE_BEHAVIOR_KEY] = behavior;
};
/**
 * Removes element draggable feature.
 *
 * @param el target element to be made draggable
 */
const removeDraggable = el => {
  el.removeAttribute(`data-${DRAGGABLE_KEY}`);
};
const stopEvent = (e, fn) => {
  var _a, _b;
  (_a = e === null || e === void 0 ? void 0 : e.stopPropagation) === null || _a === void 0 ? void 0 : _a.call(e);
  (_b = e === null || e === void 0 ? void 0 : e.preventDefault) === null || _b === void 0 ? void 0 : _b.call(e);
  fn === null || fn === void 0 ? void 0 : fn(e);
};
const stopPropagation = (e, fn) => {
  var _a;
  (_a = e === null || e === void 0 ? void 0 : e.stopPropagation) === null || _a === void 0 ? void 0 : _a.call(e);
  fn === null || fn === void 0 ? void 0 : fn(e);
};
const stopImmediatePropagation = (e, fn) => {
  var _a;
  (_a = e === null || e === void 0 ? void 0 : e.stopImmediatePropagation) === null || _a === void 0 ? void 0 : _a.call(e);
  fn === null || fn === void 0 ? void 0 : fn(e);
};
const findElementInEventPath = (e, tag) => {
  var _a, _b;
  return (_b = Array.isArray(e) ? e : (_a = e.composedPath) === null || _a === void 0 ? void 0 : _a.call(e)) === null || _b === void 0 ? void 0 : _b.find(el => {
    var _a, _b;
    return (el === null || el === void 0 ? void 0 : el.tagName) && ((_b = (_a = el.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase) === null || _b === void 0 ? void 0 : _b.call(_a)) === (tag === null || tag === void 0 ? void 0 : tag.toLowerCase());
  });
};
const findNodeInEventPath = (e, node) => {
  var _a;
  return (_a = e.composedPath) === null || _a === void 0 ? void 0 : _a.call(e).find(el => {
    var _a;
    return (_a = el === null || el === void 0 ? void 0 : el.isSameNode) === null || _a === void 0 ? void 0 : _a.call(el, node);
  });
};
const detachListeners = (el, listeners) => {
  for (const event in listeners) {
    el === null || el === void 0 ? void 0 : el.removeEventListener(event, listeners[event]);
  }
};
const attachListeners = (el, listeners, noDetach = false) => {
  !noDetach && detachListeners(el, listeners);
  for (const event in listeners) {
    el === null || el === void 0 ? void 0 : el.addEventListener(event, listeners[event]);
  }
};
const doWhenDefaultIsNotPrevented = (e, callback) => {
  var _a;
  /**
   * Calling "preventDefault" within the react managed events' handler causes callback to get called after the ancestor handler. This
   * makes unable to use "defaultPrevented" flag in the ancestor element's event handler. We fixed this issue for the owc elements
   * by adding all the events including the handled by React (native events) by using "addEventListener" in React framework
   * bindings.
   *
   * However, we are still having an issue with the native elements such as div, span and so on due to their implementation is
   * not in our control. We assume that most of the slots will be OWC elements, however, using native elements in the slots is
   * still quite common case. To fix this issue, we need to postpone calling handlers that are depending on "defaultPrevented" flag
   * in the event.
   *
   * We have several optimizations in here. Postponing the event handler calling only occurs in this condition:
   * 1. event is NOT already prevented
   * 2. framework is "react"
   * 3. the handler which calls "preventDefault" is the handler of a native element (NOT the owc element)
   */
  if (e.defaultPrevented || typeof callback !== 'function' || typeof window === 'undefined') {
    return;
  }
  if (((_a = window === null || window === void 0 ? void 0 : window[OWC_GLOBAL_KEY]) === null || _a === void 0 ? void 0 : _a[RUNTIME_FWK_GLOBAL_KEY]) === 'react') {
    requestTimeout(() => {
      !e.defaultPrevented && callback();
    });
  } else {
    callback();
  }
};
const LIST_ITEM_HEIGHT = 48;
const PERCENTAGE = 100;
const TOLARATE = 5;
const inViewport = (el, wrapperEl) => {
  const e = el === null || el === void 0 ? void 0 : el.getBoundingClientRect();
  const p = wrapperEl === null || wrapperEl === void 0 ? void 0 : wrapperEl.getBoundingClientRect();
  if (p) {
    // tolerate bottom difference up to %5 of element height
    return Math.abs(p.bottom) > Math.abs(e.bottom) || Math.abs(Math.abs(p.bottom) - Math.abs(e.bottom)) < e.height / PERCENTAGE * TOLARATE;
  } else {
    return false;
  }
};
const arrangeLimit = (limit, items) => limit > 0 ? items.slice(0, limit).reduce((prev, current) => prev + (current.offsetHeight || LIST_ITEM_HEIGHT), 0) : 0;
const doWhen = (condition, cb) => condition && cb();
const setOverflow = (container, direction, overflow) => {
  var _a;
  if ((_a = CSS === null || CSS === void 0 ? void 0 : CSS.supports) === null || _a === void 0 ? void 0 : _a.call(CSS, direction, overflow || 'overlay')) {
    container.style.setProperty(direction, overflow || 'overlay');
  }
};
function scrollToElement(item, container, align, offset, overflow) {
  doWhen(!!item && !!container, () => {
    const el = item.getBoundingClientRect();
    const content = container.getBoundingClientRect();
    const isInViewport = inViewport(item, container);
    switch (align) {
      case 'top':
      case 'bottom':
        setOverflow(container, 'overflow-y', overflow);
        doWhen(!isInViewport || el.top < content.top, () => {
          const realOffset = typeof offset === 'function' ? offset === null || offset === void 0 ? void 0 : offset() : offset;
          const scrollToTop = Math.max(0, item.offsetTop - (realOffset || 0));
          const scrollToBottom = Math.max(0, scrollToTop + el.height - content.height);
          container.scrollTop = align === 'top' ? scrollToTop : scrollToBottom;
        });
        break;
      case 'left':
      case 'right':
        setOverflow(container, 'overflow-x', overflow);
        doWhen(!isInViewport || content.width < el.left + el.width || el.left < content.left, () => {
          const realOffset = typeof offset === 'function' ? offset === null || offset === void 0 ? void 0 : offset() : offset;
          const scrollToLeft = Math.max(0, item.offsetLeft - (realOffset || 0));
          const scrollToRight = Math.max(0, scrollToLeft + el.width - content.width);
          container.scrollLeft = align === 'left' ? scrollToLeft : scrollToRight;
        });
        break;
    }
  });
}
const addClass = (el, cls) => {
  var _a;
  return el && cls && !((_a = el.classList) === null || _a === void 0 ? void 0 : _a.contains(cls)) && el.classList.add(cls);
};
const removeClass = (el, cls) => {
  var _a;
  return el && cls && ((_a = el.classList) === null || _a === void 0 ? void 0 : _a.remove(cls));
};
const getDesignTokenValue = name => {
  var _a;
  return (_a = getComputedStyleSafe(getBody())) === null || _a === void 0 ? void 0 : _a.getPropertyValue(name);
};
const createBEMElement = (element, parent = '') => [parent, element].filter(Boolean).join('__');
const createBEMModifier = (element, modifier) => [element, modifier].filter(Boolean).join('--');
var classnames = {
  exports: {}
};

/*!
	Copyright (c) 2018 Jed Watson.
	Licensed under the MIT License (MIT), see
	http://jedwatson.github.io/classnames
*/

(function (module) {
  /* global define */

  (function () {
    var hasOwn = {}.hasOwnProperty;
    function classNames() {
      var classes = [];
      for (var i = 0; i < arguments.length; i++) {
        var arg = arguments[i];
        if (!arg) continue;
        var argType = typeof arg;
        if (argType === 'string' || argType === 'number') {
          classes.push(arg);
        } else if (Array.isArray(arg)) {
          if (arg.length) {
            var inner = classNames.apply(null, arg);
            if (inner) {
              classes.push(inner);
            }
          }
        } else if (argType === 'object') {
          if (arg.toString !== Object.prototype.toString && !arg.toString.toString().includes('[native code]')) {
            classes.push(arg.toString());
            continue;
          }
          for (var key in arg) {
            if (hasOwn.call(arg, key) && arg[key]) {
              classes.push(key);
            }
          }
        }
      }
      return classes.join(' ');
    }
    if (module.exports) {
      classNames.default = classNames;
      module.exports = classNames;
    } else {
      window.classNames = classNames;
    }
  })();
})(classnames);
const classNames = classnames.exports;

/* eslint-disable no-shadow */
var FocusType;
(function (FocusType) {
  FocusType["BY_CLASS"] = "BY_CLASS";
  FocusType["BY_EVENT"] = "BY_EVENT";
})(FocusType || (FocusType = {}));

// eslint-disable-next-line
var KeyboardCode;
(function (KeyboardCode) {
  KeyboardCode["ArrowUp"] = "ArrowUp";
  KeyboardCode["ArrowDown"] = "ArrowDown";
  KeyboardCode["ArrowLeft"] = "ArrowLeft";
  KeyboardCode["ArrowRight"] = "ArrowRight";
  KeyboardCode["PageUp"] = "PageUp";
  KeyboardCode["PageDown"] = "PageDown";
  KeyboardCode["Home"] = "Home";
  KeyboardCode["End"] = "End";
  KeyboardCode["Tab"] = "Tab";
  KeyboardCode["Space"] = "Space";
  KeyboardCode["Enter"] = "Enter";
  KeyboardCode["NumpadEnter"] = "NumpadEnter";
  KeyboardCode["Escape"] = "Escape";
  KeyboardCode["EscapeAlt"] = "Esc";
  KeyboardCode["Backspace"] = "Backspace";
  KeyboardCode["Delete"] = "Delete";
})(KeyboardCode || (KeyboardCode = {}));

/* eslint-disable no-shadow */
var KeydownAction;
(function (KeydownAction) {
  KeydownAction["FIRST"] = "FIRST";
  KeydownAction["LAST"] = "LAST";
  KeydownAction["NEXT"] = "NEXT";
  KeydownAction["PREVIOUS"] = "PREVIOUS";
  KeydownAction["SELECT"] = "SELECT";
  KeydownAction["CLOSE"] = "CLOSE";
})(KeydownAction || (KeydownAction = {}));

/* eslint-disable no-shadow */
var KeydownHostEvent;
(function (KeydownHostEvent) {
  KeydownHostEvent["FOCUS"] = "FOCUS";
  KeydownHostEvent["BLUR"] = "BLUR";
  KeydownHostEvent["FOCUSIN"] = "FOCUSIN";
  KeydownHostEvent["FOCUSOUT"] = "FOCUSOUT";
  KeydownHostEvent["CLICK"] = "CLICK";
})(KeydownHostEvent || (KeydownHostEvent = {}));

/* eslint-disable no-shadow */
var KeydownLifecycleMethod;
(function (KeydownLifecycleMethod) {
  KeydownLifecycleMethod["AFTER_INIT"] = "AFTER_INIT";
  KeydownLifecycleMethod["ITEM_CHANGE"] = "ITEM_CHANGE";
})(KeydownLifecycleMethod || (KeydownLifecycleMethod = {}));

/* eslint-disable no-shadow */
var KeydownTemplate;
(function (KeydownTemplate) {
  KeydownTemplate["SIMPLE"] = "SIMPLE";
  KeydownTemplate["OVERLAY"] = "OVERLAY";
  KeydownTemplate["CLICKABLE"] = "CLICKABLE";
  KeydownTemplate["GROUP"] = "GROUP";
})(KeydownTemplate || (KeydownTemplate = {}));
var _a$1;
const SCROLL_ELEMENT_MAX_ATTEMPT = 10;
const SCROLL_ELEMENT_ATTEMPT_FREQ = 100;
const GLOBAL_FOCUS_CLASS = 'owc-soft-focus';
/* HELPER METHODS */
const doReturn = (cb, retVal) => {
  cb();
  return retVal;
};
/* IMPLEMENTATION */
class KeydownManager {
  constructor(hostElement, opts) {
    this.hostElement = hostElement;
    this.currentIndex = 0;
    this.isFocused = false;
    this.lastMouseDownEventTime = 0;
    this.focusedViaMouse = false;
    this.lastFocusedViaMouse = false;
    this.opts = {
      circular: true,
      focusType: FocusType.BY_EVENT,
      focusClass: GLOBAL_FOCUS_CLASS,
      selector: () => [],
      actualSelectedItem: () => undefined,
      hostEventHandlers: {},
      lifecycleHandlers: {},
      keydownHandlers: [],
      scroll: {
        offset: host => (host === null || host === void 0 ? void 0 : host.offsetTop) || 0
      }
    };
    /* DEFAULT KEYDOWN HANDLERS */
    this.defaultFirstHandler = props => this.setCurrentItem(this.getFirstItem(), props);
    this.defaultLastHandler = props => this.setCurrentItem(this.getLastItem(), props);
    this.defaultNextHandler = props => this.setCurrentItem(this.getNextItem(), props);
    this.defaultPreviousHandler = props => this.setCurrentItem(this.getPreviousItem(), props);
    this.defaultSelectHandler = props => {
      this.dispatchClickEvent(this.getCurrentItem());
      this.setCurrentItem(this.getCurrentItem(), props);
    };
    /* DEFAULT HOST EVENT HANDLERS */
    this.defaultFocusHandler = props => {
      var _b, _c;
      this.isFocused = true;
      if (!isSSR() && ((_b = document.activeElement) === null || _b === void 0 ? void 0 : _b.isSameNode(this.prevActiveElement))) {
        if (this.lastFocusedViaMouse) {
          return;
        }
      } else {
        this.lastFocusedViaMouse = this.focusedViaMouse;
      }
      !isSSR() && (this.prevActiveElement = document.activeElement);
      this.focusedViaMouse = false;
      const {
        items
      } = props;
      this.setCurrentItem(this.getActualSelectedItem() || (this.currentIndex >= 0 ? this.getItem(this.currentIndex, (_c = this.opts) === null || _c === void 0 ? void 0 : _c.circular) : items[0]), props, !this.lastMouseDownEventTime);
      this.lastMouseDownEventTime = 0;
    };
    this.defaultBlurHandler = () => {
      this.setStartIndex(-1);
      this.isFocused = false;
      this.prevActiveElement = null;
      this.clearFocus();
    };
    this.defaultFocusInHandler = () => undefined;
    this.defaultFocusOutHandler = () => undefined;
    this.defaultClickHandler = props => {
      const {
        items,
        event
      } = props;
      const found = items.find(item => findNodeInEventPath(event, item));
      found && this.setCurrentItem(found, props, false);
    };
    /* DEFAULT LIFECYCLE HANDLERS */
    this.defaultAfterInitHandler = () => this.clearFocus();
    this.defaultItemChangeHandler = () => undefined;
    /* DISPATCHERS */
    this.dispatchFocusEvent = item => {
      var _b;
      return (_b = item === null || item === void 0 ? void 0 : item.focus) === null || _b === void 0 ? void 0 : _b.call(item);
    };
    this.dispatchClickEvent = item => {
      var _b;
      return (_b = item === null || item === void 0 ? void 0 : item.click) === null || _b === void 0 ? void 0 : _b.call(item);
    };
    /* EVENT UTILS */
    this.getKeydownHandler = h => h ? typeof h === 'function' ? h : this.DefaultKeydownHandler[h] : undefined;
    this.getHostEventHandler = hostEvent => {
      var _b, _c;
      const h = (_c = (_b = this.opts) === null || _b === void 0 ? void 0 : _b.hostEventHandlers) === null || _c === void 0 ? void 0 : _c[hostEvent];
      return h ? typeof h === 'function' ? h : this.DefaultHostHandler[h] : undefined;
    };
    this.getLifecycleHandler = lcMethod => {
      var _b, _c;
      const h = (_c = (_b = this.opts) === null || _b === void 0 ? void 0 : _b.lifecycleHandlers) === null || _c === void 0 ? void 0 : _c[lcMethod];
      return h ? typeof h === 'function' ? h : this.DefaultLifecycleHandler[h] : undefined;
    };
    this.setPrevFocusElement = () => {
      !isSSR() && (this.prevActiveElement = document.activeElement);
    };
    this.invokeHandlerFns = (fns, props) => {
      let result;
      for (const fn of fns || []) {
        result = fn === null || fn === void 0 ? void 0 : fn(Object.assign({
          items: (props === null || props === void 0 ? void 0 : props.items) || this.getItems(),
          current: this.currentIndex,
          managerInstance: this,
          scrollBehavior: 'top'
        }, props));
      }
      return result;
    };
    /* HOST EVENT HANDLERS */
    this.onMousedown = () => {
      if (!this.isFocused) {
        this.lastMouseDownEventTime = Date.now();
        this.focusedViaMouse = true;
        this.lastFocusedViaMouse = true;
      }
    };
    this.onKeydown = event => {
      const {
        keydownHandlers
      } = this.opts;
      if (keydownHandlers.length) {
        const items = this.getItems();
        keydownHandlers.forEach(({
          key,
          handler,
          preventDefault = true,
          stopPropagation,
          scrollBehavior,
          disabled = () => false
        }) => {
          const handlerFn = this.getKeydownHandler(handler);
          if (handlerFn && _a$1.isKeyMatching(event, key)) {
            preventDefault && event.preventDefault();
            stopPropagation && event.stopPropagation();
            !disabled({
              items,
              scrollBehavior,
              event,
              current: this.currentIndex,
              managerInstance: this
            }) && this.invokeHandlerFns([handlerFn], {
              items,
              event,
              scrollBehavior
            });
          }
        });
      }
    };
    /* ITEM GETTERS */
    this.getActualSelectedItem = () => {
      var _b, _c;
      return (_c = (_b = this.opts) === null || _b === void 0 ? void 0 : _b.actualSelectedItem) === null || _c === void 0 ? void 0 : _c.call(_b, this.hostElement);
    };
    this.getItems = () => {
      var _b, _c;
      return ((_c = (_b = this.opts).selector) === null || _c === void 0 ? void 0 : _c.call(_b, this.hostElement)) || [];
    };
    this.getFirstItem = () => this.getItem(0);
    this.getLastItem = () => this.getItem(-1, true);
    this.getNextItem = () => {
      var _b;
      return this.getItem(this.currentIndex + 1, (_b = this.opts) === null || _b === void 0 ? void 0 : _b.circular);
    };
    this.getPreviousItem = () => {
      var _b;
      return this.getItem(this.currentIndex - 1, (_b = this.opts) === null || _b === void 0 ? void 0 : _b.circular);
    };
    /* KEYDOWN MANAGER UTILS */
    this.detachEventListeners = () => {
      detachListeners(this.hostElement, this.HostEventHandlers);
      !isSSR() && window.removeEventListener('blur', this.setPrevFocusElement);
    };
    this.attachEventListeners = () => {
      attachListeners(this.hostElement, this.HostEventHandlers);
      !isSSR() && window.addEventListener('blur', this.setPrevFocusElement);
    };
    this.clearFocus = () => doReturn(() => this.getItems().forEach(item => {
      var _b;
      return removeClass(item, (_b = this.opts) === null || _b === void 0 ? void 0 : _b.focusClass);
    }), this);
    this.setOptions = options => doReturn(() => this.opts = deepMerge(this.opts, options), this);
    this.setStartIndex = index => doReturn(() => this.currentIndex = index, this);
    this.getCurrentIndex = () => this.currentIndex;
    this.setCurrentIndex = i => this.setCurrentItem(this.getItems()[i]);
    this.getCurrentItem = () => this.getItems()[this.currentIndex];
    /* CONSTANT MAPPINGS */
    this.HostEventHandlers = {
      keydown: this.onKeydown,
      mousedown: this.onMousedown,
      click: event => this.invokeHandlerFns([this.getHostEventHandler(KeydownHostEvent.CLICK)], {
        event
      }),
      focus: event => this.invokeHandlerFns([this.getHostEventHandler(KeydownHostEvent.FOCUS)], {
        event
      }),
      blur: event => this.invokeHandlerFns([this.getHostEventHandler(KeydownHostEvent.BLUR)], {
        event
      }),
      focusin: event => this.invokeHandlerFns([this.getHostEventHandler(KeydownHostEvent.FOCUSIN)], {
        event
      }),
      focusout: event => this.invokeHandlerFns([this.getHostEventHandler(KeydownHostEvent.FOCUSOUT)], {
        event
      })
    };
    this.DefaultKeydownHandler = {
      [KeydownAction.FIRST]: this.defaultFirstHandler,
      [KeydownAction.LAST]: this.defaultLastHandler,
      [KeydownAction.NEXT]: this.defaultNextHandler,
      [KeydownAction.PREVIOUS]: this.defaultPreviousHandler,
      [KeydownAction.SELECT]: this.defaultSelectHandler,
      [KeydownAction.CLOSE]: () => undefined
    };
    this.DefaultHostHandler = {
      [KeydownHostEvent.FOCUS]: this.defaultFocusHandler,
      [KeydownHostEvent.BLUR]: this.defaultBlurHandler,
      [KeydownHostEvent.FOCUSIN]: this.defaultFocusInHandler,
      [KeydownHostEvent.FOCUSOUT]: this.defaultFocusOutHandler,
      [KeydownHostEvent.CLICK]: this.defaultClickHandler
    };
    this.DefaultLifecycleHandler = {
      [KeydownLifecycleMethod.AFTER_INIT]: this.defaultAfterInitHandler,
      [KeydownLifecycleMethod.ITEM_CHANGE]: this.defaultItemChangeHandler
    };
    this.opts = deepMerge(this.opts, opts);
    this.attachEventListeners();
    this.invokeHandlerFns([this.getLifecycleHandler(KeydownLifecycleMethod.AFTER_INIT)]);
  }
  scrollTo(item, scrollBehavior) {
    const {
      scroll: {
        container,
        offset
      } = {}
    } = this.opts;
    const parent = container ? container(item) : this.hostElement;
    isElementReady(() => isElementVisible(parent), SCROLL_ELEMENT_MAX_ATTEMPT, SCROLL_ELEMENT_ATTEMPT_FREQ).then(() => {
      parent && scrollToElement(item, parent, scrollBehavior || 'top', offset(this.hostElement), 'overlay');
    });
  }
  scrollToCurrentElement(scrollBehavior) {
    this.scrollTo(this.getCurrentItem(), scrollBehavior);
  }
  scrollToFirstElement(scrollBehavior) {
    this.scrollTo(this.getFirstItem(), scrollBehavior);
  }
  getItem(index, overflow = false) {
    const items = this.getItems();
    return items[overflow ? (items.length + index) % items.length : Math.max(0, Math.min(items.length - 1, index))];
  }
  setCurrentItem(item, props, shouldAddFocusToElement = true) {
    var _b;
    const {
      event,
      scrollBehavior
    } = props || {};
    const {
      focusType,
      focusClass
    } = this.opts;
    if (focusType === FocusType.BY_CLASS) {
      this.clearFocus();
      shouldAddFocusToElement && addClass(item, focusClass);
    } else if (focusType === FocusType.BY_EVENT) {
      this.dispatchFocusEvent(item);
    } else ;
    const newIndex = this.getItems().findIndex(it => {
      var _b;
      return (_b = it === null || it === void 0 ? void 0 : it.isSameNode) === null || _b === void 0 ? void 0 : _b.call(it, item);
    });
    if (item && this.currentIndex !== newIndex) {
      this.currentIndex = newIndex;
      this.scrollTo(item, scrollBehavior);
      (_b = this.getLifecycleHandler(KeydownLifecycleMethod.ITEM_CHANGE)) === null || _b === void 0 ? void 0 : _b({
        event,
        scrollBehavior,
        items: this.getItems(),
        current: this.currentIndex,
        managerInstance: this
      });
    }
    return this;
  }
}
_a$1 = KeydownManager;
/* STATICS */
KeydownManager.isKeyMatching = (e, keys) => {
  const keyConfigs = keys.map(key => typeof key === 'string' ? {
    code: key
  } : key);
  return keyConfigs.filter(cfg => (cfg.code === e.code || cfg.key === e.key) && _a$1.checkKeys(cfg, e)).length > 0;
};
KeydownManager.checkKeys = (cfg, e) => !!cfg.shift === !!e.shiftKey && !!cfg.alt === !!e.altKey && !!cfg.ctrl === !!e.ctrlKey && !!cfg.meta === !!e.metaKey;
KeydownManager.DefaultKeys = {
  [KeydownAction.FIRST]: [KeyboardCode.PageUp, KeyboardCode.Home, {
    code: KeyboardCode.ArrowUp,
    alt: true
  }, {
    code: KeyboardCode.ArrowLeft,
    alt: true
  }],
  [KeydownAction.LAST]: [KeyboardCode.PageDown, KeyboardCode.End, {
    code: KeyboardCode.ArrowDown,
    alt: true
  }, {
    code: KeyboardCode.ArrowRight,
    alt: true
  }],
  [KeydownAction.PREVIOUS]: [KeyboardCode.ArrowUp, KeyboardCode.ArrowLeft],
  [KeydownAction.NEXT]: [KeyboardCode.ArrowDown, KeyboardCode.ArrowRight],
  [KeydownAction.SELECT]: [KeyboardCode.Enter, KeyboardCode.NumpadEnter, KeyboardCode.Space],
  [KeydownAction.CLOSE]: [KeyboardCode.Escape, KeyboardCode.EscapeAlt]
};
KeydownManager.Templates = {
  [KeydownTemplate.SIMPLE]: {},
  [KeydownTemplate.OVERLAY]: {},
  [KeydownTemplate.CLICKABLE]: {
    keydownHandlers: [{
      key: _a$1.DefaultKeys[KeydownAction.SELECT],
      handler: ({
        managerInstance
      }) => {
        var _b, _c;
        return (_c = (_b = managerInstance.hostElement) === null || _b === void 0 ? void 0 : _b.click) === null || _c === void 0 ? void 0 : _c.call(_b);
      },
      preventDefault: true
    }]
  },
  [KeydownTemplate.GROUP]: {
    circular: true,
    hostEventHandlers: {
      [KeydownHostEvent.CLICK]: KeydownHostEvent.CLICK,
      [KeydownHostEvent.FOCUS]: KeydownHostEvent.FOCUS,
      [KeydownHostEvent.BLUR]: KeydownHostEvent.BLUR
    },
    lifecycleHandlers: {
      [KeydownLifecycleMethod.AFTER_INIT]: KeydownLifecycleMethod.AFTER_INIT
    },
    keydownHandlers: [{
      key: _a$1.DefaultKeys[KeydownAction.NEXT],
      handler: KeydownAction.NEXT,
      scrollBehavior: 'top'
    }, {
      key: _a$1.DefaultKeys[KeydownAction.PREVIOUS],
      handler: KeydownAction.PREVIOUS,
      scrollBehavior: 'bottom'
    }, {
      key: _a$1.DefaultKeys[KeydownAction.FIRST],
      handler: KeydownAction.FIRST,
      scrollBehavior: 'top'
    }, {
      key: _a$1.DefaultKeys[KeydownAction.LAST],
      handler: KeydownAction.LAST,
      scrollBehavior: 'bottom'
    }, {
      key: _a$1.DefaultKeys[KeydownAction.SELECT],
      handler: KeydownAction.SELECT,
      disabled: ({
        managerInstance
      }) => managerInstance.opts.focusType === FocusType.BY_EVENT,
      preventDefault: true,
      scrollBehavior: 'top'
    }]
  }
};
var _a;
class ResizeObserverSignatureClass {
  constructor(_cb) {}
  disconnect() {}
  observe(_target, _options) {}
  unobserve() {}
}
let ResizeObserver;
if (!isSSR() && window.ResizeObserver) {
  ResizeObserver = window.ResizeObserver;
} else {
  const ResizeObserverPolyfill = require('resize-observer-polyfill');
  if ((_a = ResizeObserverPolyfill.hasOwnProperty) === null || _a === void 0 ? void 0 : _a.call(ResizeObserverPolyfill, 'default')) {
    ResizeObserver = ResizeObserverPolyfill.default;
  } else {
    ResizeObserver = ResizeObserverPolyfill;
  }
}
const ResizeObserverSafe = ResizeObserver;

/* eslint-disable @typescript-eslint/no-empty-function */
class ObserverMock {
  observe() {}
  disconnect() {}
}
class MutationObserverSignatureClass {
  constructor(_cb) {}
  connect() {}
  disconnect() {}
  observe(_target, _options) {}
  unobserve() {}
  takeRecords() {
    return [];
  }
}
let MutationObserver;
if (!isSSR() && window.MutationObserver) {
  MutationObserver = window.MutationObserver;
} else {
  try {
    MutationObserver = require('mutation-observer');
  } catch (_a) {
    MutationObserver = ObserverMock;
  }
}
const MutationObserverSafe = MutationObserver;

/*
 Stencil Client Platform v4.22.3 | MIT Licensed | https://stenciljs.com
 */
var __defProp = Object.defineProperty;
var __export = (target, all) => {
  for (var name in all) __defProp(target, name, {
    get: all[name],
    enumerable: true
  });
};
var consoleError = (e, el) => (0, console.error)(e, el);
var plt = {
  $flags$: 0,
  $resourcesUrl$: "",
  jmp: h2 => h2(),
  raf: h2 => requestAnimationFrame(h2),
  ael: (el, eventName, listener, opts) => el.addEventListener(eventName, listener, opts),
  rel: (el, eventName, listener, opts) => el.removeEventListener(eventName, listener, opts),
  ce: (eventName, opts) => new CustomEvent(eventName, opts)
};
var promiseResolve = v => Promise.resolve(v);
var queuePending = false;
var queueDomReads = [];
var queueDomWrites = [];
var queueDomWritesLow = [];
var queueTask = (queue, write) => cb => {
  queue.push(cb);
  if (!queuePending) {
    queuePending = true;
    if (write && plt.$flags$ & 4 /* queueSync */) {
      nextTick(flush);
    } else {
      plt.raf(flush);
    }
  }
};
var consume = queue => {
  for (let i2 = 0; i2 < queue.length; i2++) {
    try {
      queue[i2](performance.now());
    } catch (e) {
      consoleError(e);
    }
  }
  queue.length = 0;
};
var consumeTimeout = (queue, timeout) => {
  let i2 = 0;
  let ts = 0;
  while (i2 < queue.length && (ts = performance.now()) < timeout) {
    try {
      queue[i2++](ts);
    } catch (e) {
      consoleError(e);
    }
  }
  if (i2 === queue.length) {
    queue.length = 0;
  } else if (i2 !== 0) {
    queue.splice(0, i2);
  }
};
var flush = () => {
  consume(queueDomReads);
  {
    const timeout = Infinity;
    consumeTimeout(queueDomWrites, timeout);
    consumeTimeout(queueDomWritesLow, timeout);
    if (queueDomWrites.length > 0) {
      queueDomWritesLow.push(...queueDomWrites);
      queueDomWrites.length = 0;
    }
    if (queuePending = queueDomReads.length + queueDomWrites.length + queueDomWritesLow.length > 0) {
      plt.raf(flush);
    }
  }
};
var nextTick = cb => promiseResolve().then(cb);
var readTask = /* @__PURE__ */queueTask(queueDomReads, false);
var isComplexType = o => {
  o = typeof o;
  return o === "object" || o === "function";
};

// src/utils/result.ts
var result_exports = {};
__export(result_exports, {
  err: () => err,
  map: () => map,
  ok: () => ok,
  unwrap: () => unwrap,
  unwrapErr: () => unwrapErr
});
var ok = value => ({
  isOk: true,
  isErr: false,
  value
});
var err = value => ({
  isOk: false,
  isErr: true,
  value
});
function map(result, fn) {
  if (result.isOk) {
    const val = fn(result.value);
    if (val instanceof Promise) {
      return val.then(newVal => ok(newVal));
    } else {
      return ok(val);
    }
  }
  if (result.isErr) {
    const value = result.value;
    return err(value);
  }
  throw "should never get here";
}
var unwrap = result => {
  if (result.isOk) {
    return result.value;
  } else {
    throw result.value;
  }
};
var unwrapErr = result => {
  if (result.isErr) {
    return result.value;
  } else {
    throw result.value;
  }
};
var h = (nodeName, vnodeData, ...children) => {
  let child = null;
  let key = null;
  let slotName = null;
  let simple = false;
  let lastSimple = false;
  const vNodeChildren = [];
  const walk = c => {
    for (let i2 = 0; i2 < c.length; i2++) {
      child = c[i2];
      if (Array.isArray(child)) {
        walk(child);
      } else if (child != null && typeof child !== "boolean") {
        if (simple = typeof nodeName !== "function" && !isComplexType(child)) {
          child = String(child);
        }
        if (simple && lastSimple) {
          vNodeChildren[vNodeChildren.length - 1].$text$ += child;
        } else {
          vNodeChildren.push(simple ? newVNode(null, child) : child);
        }
        lastSimple = simple;
      }
    }
  };
  walk(children);
  if (vnodeData) {
    if (vnodeData.key) {
      key = vnodeData.key;
    }
    if (vnodeData.name) {
      slotName = vnodeData.name;
    }
    {
      const classData = vnodeData.className || vnodeData.class;
      if (classData) {
        vnodeData.class = typeof classData !== "object" ? classData : Object.keys(classData).filter(k => classData[k]).join(" ");
      }
    }
  }
  if (typeof nodeName === "function") {
    return nodeName(vnodeData === null ? {} : vnodeData, vNodeChildren, vdomFnUtils);
  }
  const vnode = newVNode(nodeName, null);
  vnode.$attrs$ = vnodeData;
  if (vNodeChildren.length > 0) {
    vnode.$children$ = vNodeChildren;
  }
  {
    vnode.$key$ = key;
  }
  {
    vnode.$name$ = slotName;
  }
  return vnode;
};
var newVNode = (tag, text) => {
  const vnode = {
    $flags$: 0,
    $tag$: tag,
    $text$: text,
    $elm$: null,
    $children$: null
  };
  {
    vnode.$attrs$ = null;
  }
  {
    vnode.$key$ = null;
  }
  {
    vnode.$name$ = null;
  }
  return vnode;
};
var vdomFnUtils = {
  forEach: (children, cb) => children.map(convertToPublic).forEach(cb),
  map: (children, cb) => children.map(convertToPublic).map(cb).map(convertToPrivate)
};
var convertToPublic = node => ({
  vattrs: node.$attrs$,
  vchildren: node.$children$,
  vkey: node.$key$,
  vname: node.$name$,
  vtag: node.$tag$,
  vtext: node.$text$
});
var convertToPrivate = node => {
  if (typeof node.vtag === "function") {
    const vnodeData = {
      ...node.vattrs
    };
    if (node.vkey) {
      vnodeData.key = node.vkey;
    }
    if (node.vname) {
      vnodeData.name = node.vname;
    }
    return h(node.vtag, vnodeData, ...(node.vchildren || []));
  }
  const vnode = newVNode(node.vtag, node.vtext);
  vnode.$attrs$ = node.vattrs;
  vnode.$children$ = node.vchildren;
  vnode.$key$ = node.vkey;
  vnode.$name$ = node.vname;
  return vnode;
};
var getElement = ref => ref;
export { getRelativePosition as $, DROPZONE_BEHAVIOR_KEY as A, makeDraggable as B, CTX_KEY as C, DOC_MUTATION_OBSERVER_GLOBAL_KEY as D, makeDropzone as E, removeDraggable as F, GLOBAL_EVENTS as G, getComputedStyleSafe as H, hasEllipsis as I, isElementReady as J, isElementVisible as K, isElementInDocument as L, MutationObserverSignatureClass as M, getSiblings as N, OwcPerformanceApi as O, PERFORMANCE_API_GLOBAL_KEY as P, stopEvent as Q, ResizeObserverSignatureClass as R, SCREEN_GLOBAL_KEY as S, stopPropagation as T, stopImmediatePropagation as U, findElementInEventPath as V, findNodeInEventPath as W, detachListeners as X, attachListeners as Y, doWhenDefaultIsNotPrevented as Z, NodeType as _, getBody as a, scrollToElement as a0, addClass as a1, removeClass as a2, getDesignTokenValue as a3, createBEMElement as a4, createBEMModifier as a5, FocusType as a6, KeyboardCode as a7, KeydownAction as a8, KeydownHostEvent as a9, KeydownLifecycleMethod as aa, GLOBAL_FOCUS_CLASS as ab, KeydownManager as ac, KeydownTemplate as ad, deepMerge as ae, stringToNumberArray as af, stringToObject as ag, isAlphaNumeric as ah, isDefined as ai, isString as aj, isObject as ak, isEmptyObject as al, hasValue as am, isPositiveFloat as an, isFloat as ao, isPositiveNumber as ap, isNumber as aq, inViewport as ar, arrangeLimit as as, classNames as at, getElement as au, readTask as av, h as aw, generateElementId as b, MutationObserverSafe as c, ResizeObserverSafe as d, requestTimeout as e, OWC_GLOBAL_KEY as f, getDocument as g, RUNTIME_FWK_GLOBAL_KEY as h, isSSR as i, RUNTIME_FWK_VERSION_GLOBAL_KEY as j, RUNTIME_OWC_VERSION_GLOBAL_KEY as k, DOC_RESIZE_OBSERVER_GLOBAL_KEY as l, SCREEN_WIDTH_GLOBAL_KEY as m, SCREEN_SIZE_GLOBAL_KEY as n, PERFORMANCE_API_STORAGE_KEY as o, SUBSCRIPTIONS_KEY as p, DropzoneBehavior as q, randomString as r, setElementAttributes as s, DropBehavior as t, DRAGGING_CLASS as u, DROPZONE_CLASS as v, DROPZONE_ACTIVE_CLASS as w, DRAGGABLE_KEY as x, DRAGGABLE_UNIQUE_DATA_KEY as y, DROP_BEHAVIOR_KEY as z };